import { forwardRef, type SVGAttributes } from 'react'

export type KlimbMarkVariant = 'mesa' | 'silhouette'

export interface KlimbMarkProps extends Omit<SVGAttributes<SVGSVGElement>, 'children'> {
  size?: number | string
  variant?: KlimbMarkVariant
  inkColor?: string
  accentColor?: string
  title?: string
  decorative?: boolean
}

// KlimbMark — the Echelon logo. Three ascending peaks express "range, not
// summit" (the white-label thesis: many academies under one platform).
//
// Two variants:
//   - mesa       Flat-topped plateaus with a sage cap on the tallest peak.
//                Designed to stay legible at favicon scale (down to 14px).
//   - silhouette Merged single-polygon range; valleys never reach the
//                baseline. The most structurally durable variant.
//
// Pass `decorative` to render aria-hidden (no role, no title) for purely
// ornamental placements like background watermarks.
export const KlimbMark = forwardRef<SVGSVGElement, KlimbMarkProps>(function KlimbMark(
  {
    size = 48,
    variant = 'mesa',
    inkColor = 'var(--c-fg)',
    accentColor = 'var(--c-accent)',
    title = 'Klimb',
    decorative = false,
    ...rest
  },
  ref,
) {
  const a11y = decorative
    ? { 'aria-hidden': true as const, focusable: false as const }
    : { role: 'img' as const, 'aria-label': title }

  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 200 200"
      {...a11y}
      {...rest}
    >
      {!decorative && <title>{title}</title>}
      {variant === 'mesa' ? (
        <>
          <polygon points="14,170 80,170 70,116 24,116" fill={inkColor} />
          <polygon points="56,170 132,170 120,68 68,68" fill={inkColor} />
          <polygon points="110,170 190,170 176,16 124,16" fill={inkColor} />
          <polygon points="124,16 176,16 178,38 122,38" fill={accentColor} />
        </>
      ) : (
        <>
          <path
            d="M 10 170 L 10 130 L 60 130 L 60 80 L 110 80 L 110 30 L 160 30 L 160 170 Z"
            fill={inkColor}
          />
          <rect x="110" y="30" width="50" height="22" fill={accentColor} />
        </>
      )}
    </svg>
  )
})
